import { performanceEntries } from "../performanceEntries";
import { entries } from "../entries";

/**
 * Pattern for resource to determine whether it's considered first-party
 */
const ASSETS_PATTERN = /^https:\/\/[\w-]+\.fiverrcdn\.com\//;

/**
 * Table of patterns to match against resource names
 */
const patterns: {
    name: string;
    pattern: RegExp;
}[] = [
    { name: "javascript", pattern: /\.m?js($|\?)/ },
    { name: "stylesheets", pattern: /\.css($|\?)/ },
];

/**
 * Caclculate the hit rate in percent of files
 */
const hitRateFiles = (
    entries: PerformanceEntry[],
    cachedEntries: PerformanceEntry[],
): number => (cachedEntries.length / entries.length) * 100;

/**
 * Caclculate the hit rate in percent of bytes
 */
const hitRateBytes = (
    entries: PerformanceEntry[],
    cachedEntries: PerformanceEntry[],
): number =>
    (sum(cachedEntries, "encodedBodySize") / sum(entries, "encodedBodySize")) *
    100;

/**
 * Sum the values of the given entries by key
 * @example
 * sum([{ duration: 1 }, { duration: 2 }], 'duration') // 3
 */
const sum = (entries: PerformanceEntry[], key: string): number =>
    entries.reduce((accumulator, entry) => accumulator + entry[key], 0);

/**
 * Accumulate the hit rate of various resource types by patterns table
 */
const accumulate = (
    accumulator: Record<string, PerformanceEntry[]>,
    entry: PerformanceEntry,
): Record<string, PerformanceEntry[]> => {
    for (const { name, pattern } of patterns) {
        if (pattern.test(entry.name)) {
            accumulator[name] = accumulator[name] || [];
            accumulator[name].push(entry);
            return accumulator;
        }
    }
    return accumulator;
};

/**
 * Create performance marks for assets cache hit rate
 */
export function assetHitRate(): Record<string, number> {
    const lists = performanceEntries({ type: "resource" })
        .filter(
            ({ name }) => ASSETS_PATTERN.test(name), // Include only first-party assets
        )
        .filter(
            ({ decodedBodySize }) => decodedBodySize > 0, // Ignore assets which restrict cross-origin access to resource timing
        )
        .reduce(accumulate, {});

    const results = entries(lists)
        .filter(([, entries]) => entries.length > 0)
        .map(([name, entries]) => {
            const cachedEntries = entries.filter(
                ({ transferSize, duration }) =>
                    transferSize === 0 || duration === 0,
            );

            return {
                [`browser_cache_hit_rate_${name}_files`]: hitRateFiles(
                    entries,
                    cachedEntries,
                ),
                [`browser_cache_hit_rate_${name}_bytes`]: hitRateBytes(
                    entries,
                    cachedEntries,
                ),
            };
        });

    return Object.assign({}, ...results);
}
